import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useReferral } from '../../contexts/ReferralContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Checkbox } from '../ui/checkbox';
import { Slider } from '../ui/slider';
import { Progress } from '../ui/progress';
import { ClipboardList, AlertTriangle, Info, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== QUESTIONNAIRE DEFINITIONS ====================

// SPADI - Shoulder Pain and Disability Index (0-100, higher = worse)
const SPADI_QUESTIONS = {
    pain: [
        { id: 'p1', question: 'At its worst?' },
        { id: 'p2', question: 'When lying on the involved side?' },
        { id: 'p3', question: 'Reaching for something on a high shelf?' },
        { id: 'p4', question: 'Touching the back of your neck?' },
        { id: 'p5', question: 'Pushing with the involved arm?' }
    ],
    disability: [
        { id: 'd1', question: 'Washing your hair?' },
        { id: 'd2', question: 'Washing your back?' },
        { id: 'd3', question: 'Putting on an undershirt or pullover sweater?' },
        { id: 'd4', question: 'Putting on a shirt that buttons down the front?' },
        { id: 'd5', question: 'Putting on your pants?' },
        { id: 'd6', question: 'Placing an object on a high shelf?' },
        { id: 'd7', question: 'Carrying a heavy object of 10 pounds (4.5 kg)?' },
        { id: 'd8', question: 'Removing something from your back pocket?' }
    ]
};

// WOSI - Western Ontario Shoulder Instability Index (0-2100, lower = better)
const WOSI_QUESTIONS = {
    physical: [
        { id: 'p1', question: 'How much pain do you experience in your shoulder with overhead activities?' },
        { id: 'p2', question: 'How much aching or throbbing do you experience in your shoulder?' },
        { id: 'p3', question: 'How much weakness or lack of strength do you experience in your shoulder?' },
        { id: 'p4', question: 'How much fatigue or lack of stamina do you experience in your shoulder?' },
        { id: 'p5', question: 'How much clicking, cracking or snapping do you experience in your shoulder?' },
        { id: 'p6', question: 'How much stiffness do you experience in your shoulder?' },
        { id: 'p7', question: 'How much discomfort do you experience in your neck muscles as a result of your shoulder?' },
        { id: 'p8', question: 'How much feeling of instability or looseness do you experience in your shoulder?' },
        { id: 'p9', question: 'How much do you compensate for your shoulder with other muscles?' },
        { id: 'p10', question: 'How much loss of range of motion do you have in your shoulder?' }
    ],
    sports: [
        { id: 's1', question: 'How much has your shoulder limited the amount you can participate in sports or recreation?' },
        { id: 's2', question: 'How much has your shoulder affected your ability to perform the specific skills required for your sport or work?' },
        { id: 's3', question: 'How much do you feel the need to protect your arm during activities?' },
        { id: 's4', question: 'How much difficulty do you experience lifting heavy objects below shoulder level?' }
    ],
    lifestyle: [
        { id: 'l1', question: 'How much fear do you have of falling on your shoulder?' },
        { id: 'l2', question: 'How much difficulty do you experience maintaining your desired level of fitness?' },
        { id: 'l3', question: 'How much difficulty do you have rough housing or play fighting with family or friends?' },
        { id: 'l4', question: 'How much difficulty do you have sleeping because of your shoulder?' }
    ],
    emotions: [
        { id: 'e1', question: 'How much awareness do you have of your shoulder?' },
        { id: 'e2', question: 'How much are you concerned that your shoulder might get worse?' },
        { id: 'e3', question: 'How much frustration do you feel because of your shoulder?' }
    ]
};

// Mayo Elbow Performance Score (MEPS) - 0-100, higher = better
const MEPS_QUESTIONS = [
    {
        id: 'pain',
        question: 'Pain',
        options: [
            { score: 45, label: 'None' },
            { score: 30, label: 'Mild (with activity)' },
            { score: 15, label: 'Moderate (at rest)' },
            { score: 0, label: 'Severe (at rest, requiring medication)' }
        ]
    },
    {
        id: 'motion',
        question: 'Range of Motion (arc of motion)',
        options: [
            { score: 20, label: 'Arc >100°' },
            { score: 15, label: 'Arc 50-100°' },
            { score: 5, label: 'Arc <50°' }
        ]
    },
    {
        id: 'stability',
        question: 'Stability',
        options: [
            { score: 10, label: 'Stable' },
            { score: 5, label: 'Moderate instability' },
            { score: 0, label: 'Gross instability' }
        ]
    },
    {
        id: 'function',
        question: 'Daily Function',
        options: [
            { score: 25, label: 'Can comb hair, eat, perform hygiene, put on shoes, put on shirt' },
            { score: 20, label: 'Can perform 3-4 of the above' },
            { score: 15, label: 'Can perform 1-2 of the above' },
            { score: 0, label: 'Cannot perform any of the above' }
        ]
    }
];

// CTS-6 - Carpal Tunnel Syndrome 6-item questionnaire
const CTS6_QUESTIONS = [
    {
        id: 'numbness',
        question: 'Do you have numbness and tingling in the hand?',
        options: [
            { score: 0, label: 'No' },
            { score: 1, label: 'Mild (occasional, does not wake me)' },
            { score: 2, label: 'Moderate (frequent, may wake me)' },
            { score: 3, label: 'Severe (constant or wakes me every night)' }
        ]
    },
    {
        id: 'night_symptoms',
        question: 'Does numbness/tingling wake you at night?',
        options: [
            { score: 0, label: 'Never' },
            { score: 1, label: 'Once a week' },
            { score: 2, label: '2-3 times a week' },
            { score: 3, label: 'Every night or multiple times per night' }
        ]
    },
    {
        id: 'finger_distribution',
        question: 'Which fingers are affected?',
        options: [
            { score: 0, label: 'None / unclear pattern' },
            { score: 1, label: 'Little finger and/or ring finger only' },
            { score: 2, label: 'Multiple fingers including median nerve territory' },
            { score: 3, label: 'Thumb, index, middle, and/or ring finger (median distribution)' }
        ]
    },
    {
        id: 'weakness',
        question: 'Do you have weakness or clumsiness of the hand?',
        options: [
            { score: 0, label: 'No' },
            { score: 1, label: 'Mild (occasional dropping of objects)' },
            { score: 2, label: 'Moderate (frequent difficulty gripping)' },
            { score: 3, label: 'Severe (marked weakness, thenar atrophy)' }
        ]
    },
    {
        id: 'provocative_tests',
        question: 'Symptoms provoked by activities (driving, holding phone, etc.)?',
        options: [
            { score: 0, label: 'No' },
            { score: 1, label: 'Occasionally' },
            { score: 2, label: 'Frequently' },
            { score: 3, label: 'Always' }
        ]
    },
    {
        id: 'flick_sign',
        question: 'Do you shake/flick your hand to relieve symptoms (Flick Sign)?',
        options: [
            { score: 0, label: 'No' },
            { score: 1, label: 'Occasionally' },
            { score: 2, label: 'Frequently' },
            { score: 3, label: 'Multiple times daily' }
        ]
    }
];

// QuickDASH - Disabilities of the Arm, Shoulder and Hand (11 items)
// Score 0-100, higher = greater disability
const QUICKDASH_QUESTIONS = [
    {
        id: 'jar',
        question: 'Open a tight or new jar',
        options: [
            { score: 1, label: 'No difficulty' },
            { score: 2, label: 'Mild difficulty' },
            { score: 3, label: 'Moderate difficulty' },
            { score: 4, label: 'Severe difficulty' },
            { score: 5, label: 'Unable' }
        ]
    },
    {
        id: 'write',
        question: 'Write',
        options: [
            { score: 1, label: 'No difficulty' },
            { score: 2, label: 'Mild difficulty' },
            { score: 3, label: 'Moderate difficulty' },
            { score: 4, label: 'Severe difficulty' },
            { score: 5, label: 'Unable' }
        ]
    },
    {
        id: 'turn_key',
        question: 'Turn a key',
        options: [
            { score: 1, label: 'No difficulty' },
            { score: 2, label: 'Mild difficulty' },
            { score: 3, label: 'Moderate difficulty' },
            { score: 4, label: 'Severe difficulty' },
            { score: 5, label: 'Unable' }
        ]
    },
    {
        id: 'prepare_meal',
        question: 'Prepare a meal',
        options: [
            { score: 1, label: 'No difficulty' },
            { score: 2, label: 'Mild difficulty' },
            { score: 3, label: 'Moderate difficulty' },
            { score: 4, label: 'Severe difficulty' },
            { score: 5, label: 'Unable' }
        ]
    },
    {
        id: 'push_door',
        question: 'Push open a heavy door',
        options: [
            { score: 1, label: 'No difficulty' },
            { score: 2, label: 'Mild difficulty' },
            { score: 3, label: 'Moderate difficulty' },
            { score: 4, label: 'Severe difficulty' },
            { score: 5, label: 'Unable' }
        ]
    },
    {
        id: 'shelf',
        question: 'Place an object on a shelf above your head',
        options: [
            { score: 1, label: 'No difficulty' },
            { score: 2, label: 'Mild difficulty' },
            { score: 3, label: 'Moderate difficulty' },
            { score: 4, label: 'Severe difficulty' },
            { score: 5, label: 'Unable' }
        ]
    },
    {
        id: 'heavy_chores',
        question: 'Do heavy household chores (e.g., wash walls, floors)',
        options: [
            { score: 1, label: 'No difficulty' },
            { score: 2, label: 'Mild difficulty' },
            { score: 3, label: 'Moderate difficulty' },
            { score: 4, label: 'Severe difficulty' },
            { score: 5, label: 'Unable' }
        ]
    },
    {
        id: 'garden',
        question: 'Garden or do yard work',
        options: [
            { score: 1, label: 'No difficulty' },
            { score: 2, label: 'Mild difficulty' },
            { score: 3, label: 'Moderate difficulty' },
            { score: 4, label: 'Severe difficulty' },
            { score: 5, label: 'Unable' }
        ]
    },
    {
        id: 'make_bed',
        question: 'Make a bed',
        options: [
            { score: 1, label: 'No difficulty' },
            { score: 2, label: 'Mild difficulty' },
            { score: 3, label: 'Moderate difficulty' },
            { score: 4, label: 'Severe difficulty' },
            { score: 5, label: 'Unable' }
        ]
    },
    {
        id: 'bag',
        question: 'Carry a shopping bag or briefcase',
        options: [
            { score: 1, label: 'No difficulty' },
            { score: 2, label: 'Mild difficulty' },
            { score: 3, label: 'Moderate difficulty' },
            { score: 4, label: 'Severe difficulty' },
            { score: 5, label: 'Unable' }
        ]
    },
    {
        id: 'wash_back',
        question: 'Wash your back',
        options: [
            { score: 1, label: 'No difficulty' },
            { score: 2, label: 'Mild difficulty' },
            { score: 3, label: 'Moderate difficulty' },
            { score: 4, label: 'Severe difficulty' },
            { score: 5, label: 'Unable' }
        ]
    }
];

export default function QuestionnaireStep() {
    const { 
        body_part, 
        condition, 
        questionnaire_scores, 
        updateQuestionnaireScores 
    } = useReferral();
    
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState(true);

    // Check if this is a Prosthetic Joint Problem condition
    const isPJP = condition && (
        condition === 'prosthetic_joint_problem_hip' ||
        condition === 'prosthetic_joint_problem_knee' ||
        condition === 'prosthetic_joint_problem_shoulder'
    );

    // Determine which questionnaire(s) to show based on condition
    const getQuestionnaireTypes = () => {
        const types = [];
        
        // PJP conditions use ABRIDGED questionnaires
        if (isPJP) {
            if (condition === 'prosthetic_joint_problem_hip') {
                types.push('oxford-hip-abridged');
            } else if (condition === 'prosthetic_joint_problem_knee') {
                types.push('oxford-knee-abridged');
            } else if (condition === 'prosthetic_joint_problem_shoulder') {
                types.push('ases-abridged');
            }
            return types;
        }
        
        // Standard conditions use full questionnaires
        // Hip conditions
        if (condition.includes('hip')) types.push('oxford-hip');
        
        // Knee conditions
        if (condition.includes('knee')) types.push('oxford-knee');
        
        // Spine conditions
        if (body_part === 'spine') types.push('odi');
        
        // Shoulder conditions - SPADI for most, WOSI for instability
        if (condition === 'shoulder_instability' || condition === 'recurrent_dislocation') {
            types.push('wosi');
        } else if (['rotator_cuff_tear', 'impingement_syndrome', 'adhesive_capsulitis', 'shoulder_arthritis'].includes(condition) || body_part === 'shoulder') {
            types.push('spadi');
        }
        
        // Elbow conditions - MEPS
        if (body_part === 'elbow' || ['tennis_elbow', 'golfers_elbow', 'elbow_arthritis'].includes(condition)) {
            types.push('meps');
        }
        
        // Carpal tunnel - CTS-6
        if (condition === 'carpal_tunnel_syndrome' || (body_part === 'wrist' && condition.includes('carpal'))) {
            types.push('cts6');
        }
        
        // Hand and wrist conditions - QuickDASH (except carpal tunnel which uses CTS-6)
        if ((body_part === 'hand' || body_part === 'wrist') && condition !== 'carpal_tunnel_syndrome') {
            types.push('quickdash');
        }
        
        return types;
    };

    const questionnaireTypes = getQuestionnaireTypes();

    useEffect(() => {
        const fetchData = async () => {
            // Fetch standard questionnaires from API if needed
            for (const qType of questionnaireTypes) {
                // Include abridged versions in the fetch list
                if (['oxford-hip', 'oxford-knee', 'odi', 'oxford-hip-abridged', 'oxford-knee-abridged', 'ases-abridged'].includes(qType)) {
                    try {
                        const response = await axios.get(`${API}/questionnaires/${qType}`);
                        setQuestions(prev => ({...prev, [qType]: response.data.questions}));
                    } catch (error) {
                        console.error(`Failed to fetch ${qType}:`, error);
                    }
                }
            }
            setLoading(false);
        };
        
        if (questionnaireTypes.length > 0) {
            fetchData();
        } else {
            setLoading(false);
        }
    }, [condition, body_part]);

    // SPADI scoring (0-100, higher = worse)
    const calculateSPADI = (painAnswers, disabilityAnswers) => {
        const painSum = Object.values(painAnswers).reduce((sum, val) => sum + (val || 0), 0);
        const disabilitySum = Object.values(disabilityAnswers).reduce((sum, val) => sum + (val || 0), 0);
        const painScore = (painSum / (SPADI_QUESTIONS.pain.length * 10)) * 100;
        const disabilityScore = (disabilitySum / (SPADI_QUESTIONS.disability.length * 10)) * 100;
        return Math.round((painScore + disabilityScore) / 2);
    };

    // WOSI scoring (0-2100, convert to percentage)
    const calculateWOSI = (wAnswers) => {
        const total = Object.values(wAnswers).reduce((sum, val) => sum + (val || 0), 0);
        return Math.round((total / 2100) * 100);
    };

    // MEPS scoring (0-100, higher = better)
    const calculateMEPS = (mepsAnswers) => {
        return Object.values(mepsAnswers).reduce((sum, val) => sum + (val || 0), 0);
    };

    // CTS-6 scoring (0-18, higher = more likely CTS)
    const calculateCTS6 = (cts6Answers) => {
        return Object.values(cts6Answers).reduce((sum, val) => sum + (val || 0), 0);
    };

    // QuickDASH scoring (0-100, higher = greater disability)
    // Formula: ((sum of n responses / n) - 1) x 25
    const calculateQuickDASH = (quickdashAnswers) => {
        const responses = Object.values(quickdashAnswers).filter(v => v !== undefined && v !== null);
        if (responses.length < 10) return null; // Need at least 10 of 11 responses
        const sum = responses.reduce((acc, val) => acc + val, 0);
        return Math.round(((sum / responses.length) - 1) * 25);
    };

    const handleSPADIChange = (section, questionId, value) => {
        const key = `spadi_${section}`;
        const currentAnswers = questionnaire_scores[key] || {};
        const newAnswers = { ...currentAnswers, [questionId]: value };
        
        updateQuestionnaireScores({ [key]: newAnswers });
        
        // Calculate total score if enough answers
        const painAnswers = section === 'pain' ? newAnswers : (questionnaire_scores.spadi_pain || {});
        const disabilityAnswers = section === 'disability' ? newAnswers : (questionnaire_scores.spadi_disability || {});
        
        const painCount = Object.keys(painAnswers).length;
        const disabilityCount = Object.keys(disabilityAnswers).length;
        
        if (painCount >= 3 && disabilityCount >= 5) {
            const score = calculateSPADI(painAnswers, disabilityAnswers);
            updateQuestionnaireScores({ spadi_score: score });
        }
    };

    const handleWOSIChange = (section, questionId, value) => {
        const key = `wosi_${section}`;
        const currentAnswers = questionnaire_scores[key] || {};
        const newAnswers = { ...currentAnswers, [questionId]: value };
        
        updateQuestionnaireScores({ [key]: newAnswers });
        
        // Calculate total
        const allAnswers = {
            ...(section === 'physical' ? newAnswers : (questionnaire_scores.wosi_physical || {})),
            ...(section === 'sports' ? newAnswers : (questionnaire_scores.wosi_sports || {})),
            ...(section === 'lifestyle' ? newAnswers : (questionnaire_scores.wosi_lifestyle || {})),
            ...(section === 'emotions' ? newAnswers : (questionnaire_scores.wosi_emotions || {}))
        };
        
        if (Object.keys(allAnswers).length >= 15) {
            const score = calculateWOSI(allAnswers);
            updateQuestionnaireScores({ wosi_score: score });
        }
    };

    const handleMEPSChange = (questionId, score) => {
        const currentAnswers = questionnaire_scores.meps_answers || {};
        const newAnswers = { ...currentAnswers, [questionId]: score };
        
        updateQuestionnaireScores({ meps_answers: newAnswers });
        
        if (Object.keys(newAnswers).length === MEPS_QUESTIONS.length) {
            const totalScore = calculateMEPS(newAnswers);
            updateQuestionnaireScores({ meps_score: totalScore });
        }
    };

    const handleCTS6Change = (questionId, score) => {
        const currentAnswers = questionnaire_scores.cts6_answers || {};
        const newAnswers = { ...currentAnswers, [questionId]: score };
        
        updateQuestionnaireScores({ cts6_answers: newAnswers });
        
        if (Object.keys(newAnswers).length === CTS6_QUESTIONS.length) {
            const totalScore = calculateCTS6(newAnswers);
            updateQuestionnaireScores({ cts6_score: totalScore });
        }
    };

    const handleQuickDASHChange = (questionId, score) => {
        const currentAnswers = questionnaire_scores.quickdash_answers || {};
        const newAnswers = { ...currentAnswers, [questionId]: score };
        
        updateQuestionnaireScores({ quickdash_answers: newAnswers });
        
        // Calculate score when we have at least 10 responses
        if (Object.keys(newAnswers).length >= 10) {
            const totalScore = calculateQuickDASH(newAnswers);
            if (totalScore !== null) {
                updateQuestionnaireScores({ quickdash_score: totalScore });
            }
        }
    };

    const handleVASChange = (value) => {
        updateQuestionnaireScores({ vas_pain_score: value[0] });
    };

    const handleRedFlagChange = (flag, checked) => {
        const currentFlags = questionnaire_scores.bss_red_flags || [];
        if (checked) {
            updateQuestionnaireScores({ bss_red_flags: [...currentFlags, flag] });
        } else {
            updateQuestionnaireScores({ bss_red_flags: currentFlags.filter(f => f !== flag) });
        }
    };

    // Get score interpretations
    const getSPADIInterpretation = (score) => {
        if (score === null || score === undefined) return null;
        if (score >= 50) return { text: 'Significant disability - meets referral threshold', color: 'text-rose-600', pass: true };
        if (score >= 30) return { text: 'Moderate disability', color: 'text-amber-600', pass: true };
        return { text: 'Mild disability - may not meet referral criteria', color: 'text-emerald-600', pass: false };
    };

    const getWOSIInterpretation = (score) => {
        if (score === null || score === undefined) return null;
        if (score >= 50) return { text: 'Significant instability impact - meets referral threshold', color: 'text-rose-600', pass: true };
        if (score >= 30) return { text: 'Moderate impact from instability', color: 'text-amber-600', pass: true };
        return { text: 'Mild impact - conservative management may be appropriate', color: 'text-emerald-600', pass: false };
    };

    const getMEPSInterpretation = (score) => {
        if (score === null || score === undefined) return null;
        if (score <= 60) return { text: 'Poor function (≤60) - meets referral criteria', color: 'text-rose-600', pass: true };
        if (score <= 75) return { text: 'Fair function (61-75)', color: 'text-amber-600', pass: false };
        if (score <= 90) return { text: 'Good function (76-90)', color: 'text-emerald-600', pass: false };
        return { text: 'Excellent function (>90) - may not require referral', color: 'text-emerald-600', pass: false };
    };

    const getCTS6Interpretation = (score) => {
        if (score === null || score === undefined) return null;
        if (score >= 12) return { text: 'High probability of CTS - meets referral criteria', color: 'text-rose-600', pass: true };
        if (score >= 8) return { text: 'Moderate probability of CTS', color: 'text-amber-600', pass: true };
        return { text: 'Low probability of CTS - consider other diagnoses', color: 'text-emerald-600', pass: false };
    };

    const getQuickDASHInterpretation = (score) => {
        if (score === null || score === undefined) return null;
        if (score >= 60) return { text: 'Severe disability - meets referral criteria', color: 'text-rose-600', pass: true };
        if (score >= 40) return { text: 'Moderate disability - meets referral criteria', color: 'text-amber-600', pass: true };
        if (score >= 20) return { text: 'Mild disability', color: 'text-amber-600', pass: false };
        return { text: 'Minimal disability - may not require referral', color: 'text-emerald-600', pass: false };
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* VAS Pain Score - Always shown */}
            <Card className="card-clinical">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <ClipboardList className="w-5 h-5 text-teal-600" />
                        Pain Assessment (VAS)
                    </CardTitle>
                    <CardDescription>
                        Visual Analogue Scale: Rate the patient's typical pain level
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-6">
                        <div className="space-y-4">
                            <div className="flex justify-between text-sm text-slate-500">
                                <span>No pain</span>
                                <span>Worst imaginable pain</span>
                            </div>
                            <Slider
                                value={[questionnaire_scores.vas_pain_score || 0]}
                                onValueChange={handleVASChange}
                                max={10}
                                step={1}
                                className="w-full"
                                data-testid="vas-slider"
                            />
                            <div className="flex justify-between text-xs text-slate-400">
                                {[...Array(11)].map((_, i) => (
                                    <span key={i}>{i}</span>
                                ))}
                            </div>
                        </div>
                        <div className={`text-center p-4 rounded-lg ${
                            questionnaire_scores.vas_pain_score >= 7 
                                ? 'bg-rose-50 text-rose-700' 
                                : questionnaire_scores.vas_pain_score >= 4
                                ? 'bg-amber-50 text-amber-700'
                                : 'bg-emerald-50 text-emerald-700'
                        }`}>
                            <span className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                {questionnaire_scores.vas_pain_score ?? 0}
                            </span>
                            <span className="text-lg">/10</span>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Spinal Red Flags */}
            {body_part === 'spine' && (
                <Card className="card-clinical border-rose-200 bg-rose-50/30">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-rose-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            <AlertTriangle className="w-5 h-5 text-rose-600" />
                            Spinal Red Flags (BSS)
                        </CardTitle>
                        <CardDescription className="text-rose-600">
                            Check any red flags present - these indicate urgent review
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[
                                { id: 'cauda_equina_syndrome', label: 'Cauda Equina Syndrome (bladder/bowel dysfunction, saddle anaesthesia)' },
                                { id: 'progressive_neurological_deficit', label: 'Progressive neurological deficit' },
                                { id: 'severe_unremitting_pain', label: 'Severe unremitting pain (not relieved by rest)' },
                                { id: 'unexplained_weight_loss', label: 'Unexplained weight loss' },
                                { id: 'history_of_cancer', label: 'History of cancer' },
                                { id: 'fever_infection', label: 'Fever or signs of infection' },
                                { id: 'recent_significant_trauma', label: 'Recent significant trauma' },
                                { id: 'age_under_20_or_over_55_new_onset', label: 'Age <20 or >55 with new onset' },
                                { id: 'structural_deformity', label: 'Structural spinal deformity' },
                                { id: 'immunosuppression', label: 'Immunosuppression' }
                            ].map((flag) => (
                                <div key={flag.id} className="flex items-start space-x-3">
                                    <Checkbox
                                        id={flag.id}
                                        checked={(questionnaire_scores.bss_red_flags || []).includes(flag.id)}
                                        onCheckedChange={(checked) => handleRedFlagChange(flag.id, checked)}
                                        data-testid={`red-flag-${flag.id}`}
                                    />
                                    <Label htmlFor={flag.id} className="text-sm text-rose-800 cursor-pointer leading-tight">
                                        {flag.label}
                                    </Label>
                                </div>
                            ))}
                        </div>
                        {(questionnaire_scores.bss_red_flags || []).length > 0 && (
                            <div className="mt-4 p-3 bg-rose-100 rounded-lg">
                                <p className="text-sm text-rose-800 font-medium flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4" />
                                    {questionnaire_scores.bss_red_flags.length} red flag(s) - flagged for urgent review
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Oxford Hip Score */}
            {questionnaireTypes.includes('oxford-hip') && questions['oxford-hip'] && (
                <Card className="card-clinical">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            <ClipboardList className="w-5 h-5 text-teal-600" />
                            Oxford Hip Score (OHS)
                            <span className="text-xs font-normal text-red-500 ml-2">*Required</span>
                        </CardTitle>
                        <CardDescription>
                            Score 0-48. Higher score = better function. Referral threshold: ≤39/48
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Score display */}
                        {questionnaire_scores.oxford_hip_score !== undefined && (
                            <div className={`p-4 rounded-lg border ${
                                questionnaire_scores.oxford_hip_score <= 29 ? 'bg-rose-50 border-rose-200' :
                                questionnaire_scores.oxford_hip_score <= 39 ? 'bg-amber-50 border-amber-200' :
                                'bg-emerald-50 border-emerald-200'
                            }`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className={`font-semibold ${
                                            questionnaire_scores.oxford_hip_score <= 29 ? 'text-rose-600' :
                                            questionnaire_scores.oxford_hip_score <= 39 ? 'text-amber-600' :
                                            'text-emerald-600'
                                        }`}>
                                            {questionnaire_scores.oxford_hip_score <= 29 ? 'Severe hip dysfunction - meets referral criteria' :
                                             questionnaire_scores.oxford_hip_score <= 39 ? 'Moderate hip dysfunction - meets referral criteria' :
                                             'Mild/Good function - may not meet referral criteria'}
                                        </p>
                                        <p className="text-xs text-slate-500">OHS: Higher = better function (0-48)</p>
                                    </div>
                                    <div className={`text-3xl font-bold ${
                                        questionnaire_scores.oxford_hip_score <= 29 ? 'text-rose-600' :
                                        questionnaire_scores.oxford_hip_score <= 39 ? 'text-amber-600' :
                                        'text-emerald-600'
                                    }`}>
                                        {questionnaire_scores.oxford_hip_score}/48
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Questions */}
                        <div className="space-y-4">
                            {questions['oxford-hip'].map((q, idx) => (
                                <div key={q.id} className="p-4 bg-slate-50 rounded-lg">
                                    <Label className="font-medium text-slate-800 mb-3 block">
                                        {idx + 1}. {q.question}
                                    </Label>
                                    <RadioGroup
                                        value={(questionnaire_scores.oxford_hip_answers || {})[q.id]?.toString()}
                                        onValueChange={(value) => {
                                            const currentAnswers = questionnaire_scores.oxford_hip_answers || {};
                                            const newAnswers = { ...currentAnswers, [q.id]: parseInt(value) };
                                            updateQuestionnaireScores({ oxford_hip_answers: newAnswers });
                                            
                                            // Calculate score if all questions answered
                                            if (Object.keys(newAnswers).length === questions['oxford-hip'].length) {
                                                const total = Object.values(newAnswers).reduce((sum, val) => sum + val, 0);
                                                updateQuestionnaireScores({ oxford_hip_score: total });
                                            }
                                        }}
                                        className="space-y-2"
                                    >
                                        {q.options.map((option, optIdx) => (
                                            <div
                                                key={optIdx}
                                                className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer ${
                                                    (questionnaire_scores.oxford_hip_answers || {})[q.id] === (4 - optIdx)
                                                        ? 'border-teal-500 bg-teal-50'
                                                        : 'border-slate-200 bg-white hover:border-slate-300'
                                                }`}
                                                onClick={() => {
                                                    const currentAnswers = questionnaire_scores.oxford_hip_answers || {};
                                                    const newAnswers = { ...currentAnswers, [q.id]: 4 - optIdx };
                                                    updateQuestionnaireScores({ oxford_hip_answers: newAnswers });
                                                    
                                                    if (Object.keys(newAnswers).length === questions['oxford-hip'].length) {
                                                        const total = Object.values(newAnswers).reduce((sum, val) => sum + val, 0);
                                                        updateQuestionnaireScores({ oxford_hip_score: total });
                                                    }
                                                }}
                                            >
                                                <RadioGroupItem value={(4 - optIdx).toString()} id={`ohs-${q.id}-${optIdx}`} />
                                                <Label className="flex-1 cursor-pointer text-sm">
                                                    {option}
                                                    <span className="text-xs text-slate-400 ml-2">({4 - optIdx} pts)</span>
                                                </Label>
                                            </div>
                                        ))}
                                    </RadioGroup>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Oxford Hip Score - ABRIDGED (for PJP) */}
            {questionnaireTypes.includes('oxford-hip-abridged') && questions['oxford-hip-abridged'] && (
                <Card className="card-clinical border-blue-200 bg-blue-50/30">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            <ClipboardList className="w-5 h-5 text-blue-600" />
                            Oxford Hip Score - Abridged (PJP)
                            <span className="text-xs font-normal text-red-500 ml-2">*Required</span>
                        </CardTitle>
                        <CardDescription>
                            6-item questionnaire for prosthetic joint assessment. Score 0-24.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Score display */}
                        {questionnaire_scores.oxford_hip_abridged_score !== undefined && (
                            <div className={`p-4 rounded-lg border ${
                                questionnaire_scores.oxford_hip_abridged_score <= 12 ? 'bg-rose-50 border-rose-200' :
                                questionnaire_scores.oxford_hip_abridged_score <= 18 ? 'bg-amber-50 border-amber-200' :
                                'bg-emerald-50 border-emerald-200'
                            }`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className={`font-semibold ${
                                            questionnaire_scores.oxford_hip_abridged_score <= 12 ? 'text-rose-600' :
                                            questionnaire_scores.oxford_hip_abridged_score <= 18 ? 'text-amber-600' :
                                            'text-emerald-600'
                                        }`}>
                                            {questionnaire_scores.oxford_hip_abridged_score <= 12 ? 'Significant prosthetic dysfunction' :
                                             questionnaire_scores.oxford_hip_abridged_score <= 18 ? 'Moderate prosthetic dysfunction' :
                                             'Mild/Good prosthetic function'}
                                        </p>
                                        <p className="text-xs text-slate-500">Abridged OHS: Higher = better function (0-24)</p>
                                    </div>
                                    <div className={`text-3xl font-bold ${
                                        questionnaire_scores.oxford_hip_abridged_score <= 12 ? 'text-rose-600' :
                                        questionnaire_scores.oxford_hip_abridged_score <= 18 ? 'text-amber-600' :
                                        'text-emerald-600'
                                    }`}>
                                        {questionnaire_scores.oxford_hip_abridged_score}/24
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Questions */}
                        <div className="space-y-4">
                            {questions['oxford-hip-abridged'].map((q, idx) => (
                                <div key={q.id} className="p-4 bg-white rounded-lg border border-slate-200">
                                    <Label className="font-medium text-slate-800 mb-3 block">
                                        {idx + 1}. {q.question}
                                    </Label>
                                    <RadioGroup
                                        value={(questionnaire_scores.oxford_hip_abridged_answers || {})[q.id]?.toString()}
                                        onValueChange={(value) => {
                                            const currentAnswers = questionnaire_scores.oxford_hip_abridged_answers || {};
                                            const newAnswers = { ...currentAnswers, [q.id]: parseInt(value) };
                                            updateQuestionnaireScores({ oxford_hip_abridged_answers: newAnswers });
                                            
                                            // Calculate score if all questions answered (6 questions)
                                            if (Object.keys(newAnswers).length === questions['oxford-hip-abridged'].length) {
                                                const total = Object.values(newAnswers).reduce((sum, val) => sum + val, 0);
                                                updateQuestionnaireScores({ oxford_hip_abridged_score: total });
                                            }
                                        }}
                                        className="space-y-2"
                                    >
                                        {q.options.map((option, optIdx) => (
                                            <div
                                                key={optIdx}
                                                className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer ${
                                                    (questionnaire_scores.oxford_hip_abridged_answers || {})[q.id] === (4 - optIdx)
                                                        ? 'border-blue-500 bg-blue-50'
                                                        : 'border-slate-200 bg-white hover:border-slate-300'
                                                }`}
                                                onClick={() => {
                                                    const currentAnswers = questionnaire_scores.oxford_hip_abridged_answers || {};
                                                    const newAnswers = { ...currentAnswers, [q.id]: 4 - optIdx };
                                                    updateQuestionnaireScores({ oxford_hip_abridged_answers: newAnswers });
                                                    
                                                    if (Object.keys(newAnswers).length === questions['oxford-hip-abridged'].length) {
                                                        const total = Object.values(newAnswers).reduce((sum, val) => sum + val, 0);
                                                        updateQuestionnaireScores({ oxford_hip_abridged_score: total });
                                                    }
                                                }}
                                            >
                                                <RadioGroupItem value={(4 - optIdx).toString()} id={`ohs-abr-${q.id}-${optIdx}`} />
                                                <Label className="flex-1 cursor-pointer text-sm">
                                                    {option}
                                                    <span className="text-xs text-slate-400 ml-2">({4 - optIdx} pts)</span>
                                                </Label>
                                            </div>
                                        ))}
                                    </RadioGroup>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Oxford Knee Score */}
            {questionnaireTypes.includes('oxford-knee') && questions['oxford-knee'] && (
                <Card className="card-clinical">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            <ClipboardList className="w-5 h-5 text-teal-600" />
                            Oxford Knee Score (OKS)
                            <span className="text-xs font-normal text-red-500 ml-2">*Required</span>
                        </CardTitle>
                        <CardDescription>
                            Score 0-48. Higher score = better function. Referral threshold: ≤39/48
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Score display */}
                        {questionnaire_scores.oxford_knee_score !== undefined && (
                            <div className={`p-4 rounded-lg border ${
                                questionnaire_scores.oxford_knee_score <= 29 ? 'bg-rose-50 border-rose-200' :
                                questionnaire_scores.oxford_knee_score <= 39 ? 'bg-amber-50 border-amber-200' :
                                'bg-emerald-50 border-emerald-200'
                            }`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className={`font-semibold ${
                                            questionnaire_scores.oxford_knee_score <= 29 ? 'text-rose-600' :
                                            questionnaire_scores.oxford_knee_score <= 39 ? 'text-amber-600' :
                                            'text-emerald-600'
                                        }`}>
                                            {questionnaire_scores.oxford_knee_score <= 29 ? 'Severe knee dysfunction - meets referral criteria' :
                                             questionnaire_scores.oxford_knee_score <= 39 ? 'Moderate knee dysfunction - meets referral criteria' :
                                             'Mild/Good function - may not meet referral criteria'}
                                        </p>
                                        <p className="text-xs text-slate-500">OKS: Higher = better function (0-48)</p>
                                    </div>
                                    <div className={`text-3xl font-bold ${
                                        questionnaire_scores.oxford_knee_score <= 29 ? 'text-rose-600' :
                                        questionnaire_scores.oxford_knee_score <= 39 ? 'text-amber-600' :
                                        'text-emerald-600'
                                    }`}>
                                        {questionnaire_scores.oxford_knee_score}/48
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Questions */}
                        <div className="space-y-4">
                            {questions['oxford-knee'].map((q, idx) => (
                                <div key={q.id} className="p-4 bg-slate-50 rounded-lg">
                                    <Label className="font-medium text-slate-800 mb-3 block">
                                        {idx + 1}. {q.question}
                                    </Label>
                                    <RadioGroup
                                        value={(questionnaire_scores.oxford_knee_answers || {})[q.id]?.toString()}
                                        onValueChange={(value) => {
                                            const currentAnswers = questionnaire_scores.oxford_knee_answers || {};
                                            const newAnswers = { ...currentAnswers, [q.id]: parseInt(value) };
                                            updateQuestionnaireScores({ oxford_knee_answers: newAnswers });
                                            
                                            // Calculate score if all questions answered
                                            if (Object.keys(newAnswers).length === questions['oxford-knee'].length) {
                                                const total = Object.values(newAnswers).reduce((sum, val) => sum + val, 0);
                                                updateQuestionnaireScores({ oxford_knee_score: total });
                                            }
                                        }}
                                        className="space-y-2"
                                    >
                                        {q.options.map((option, optIdx) => (
                                            <div
                                                key={optIdx}
                                                className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer ${
                                                    (questionnaire_scores.oxford_knee_answers || {})[q.id] === (4 - optIdx)
                                                        ? 'border-teal-500 bg-teal-50'
                                                        : 'border-slate-200 bg-white hover:border-slate-300'
                                                }`}
                                                onClick={() => {
                                                    const currentAnswers = questionnaire_scores.oxford_knee_answers || {};
                                                    const newAnswers = { ...currentAnswers, [q.id]: 4 - optIdx };
                                                    updateQuestionnaireScores({ oxford_knee_answers: newAnswers });
                                                    
                                                    if (Object.keys(newAnswers).length === questions['oxford-knee'].length) {
                                                        const total = Object.values(newAnswers).reduce((sum, val) => sum + val, 0);
                                                        updateQuestionnaireScores({ oxford_knee_score: total });
                                                    }
                                                }}
                                            >
                                                <RadioGroupItem value={(4 - optIdx).toString()} id={`oks-${q.id}-${optIdx}`} />
                                                <Label className="flex-1 cursor-pointer text-sm">
                                                    {option}
                                                    <span className="text-xs text-slate-400 ml-2">({4 - optIdx} pts)</span>
                                                </Label>
                                            </div>
                                        ))}
                                    </RadioGroup>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Oxford Knee Score - ABRIDGED (for PJP) */}
            {questionnaireTypes.includes('oxford-knee-abridged') && questions['oxford-knee-abridged'] && (
                <Card className="card-clinical border-blue-200 bg-blue-50/30">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            <ClipboardList className="w-5 h-5 text-blue-600" />
                            Oxford Knee Score - Abridged (PJP)
                            <span className="text-xs font-normal text-red-500 ml-2">*Required</span>
                        </CardTitle>
                        <CardDescription>
                            6-item questionnaire for prosthetic knee assessment. Score 0-24.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Score display */}
                        {questionnaire_scores.oxford_knee_abridged_score !== undefined && (
                            <div className={`p-4 rounded-lg border ${
                                questionnaire_scores.oxford_knee_abridged_score <= 12 ? 'bg-rose-50 border-rose-200' :
                                questionnaire_scores.oxford_knee_abridged_score <= 18 ? 'bg-amber-50 border-amber-200' :
                                'bg-emerald-50 border-emerald-200'
                            }`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className={`font-semibold ${
                                            questionnaire_scores.oxford_knee_abridged_score <= 12 ? 'text-rose-600' :
                                            questionnaire_scores.oxford_knee_abridged_score <= 18 ? 'text-amber-600' :
                                            'text-emerald-600'
                                        }`}>
                                            {questionnaire_scores.oxford_knee_abridged_score <= 12 ? 'Significant prosthetic dysfunction' :
                                             questionnaire_scores.oxford_knee_abridged_score <= 18 ? 'Moderate prosthetic dysfunction' :
                                             'Mild/Good prosthetic function'}
                                        </p>
                                        <p className="text-xs text-slate-500">Abridged OKS: Higher = better function (0-24)</p>
                                    </div>
                                    <div className={`text-3xl font-bold ${
                                        questionnaire_scores.oxford_knee_abridged_score <= 12 ? 'text-rose-600' :
                                        questionnaire_scores.oxford_knee_abridged_score <= 18 ? 'text-amber-600' :
                                        'text-emerald-600'
                                    }`}>
                                        {questionnaire_scores.oxford_knee_abridged_score}/24
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Questions */}
                        <div className="space-y-4">
                            {questions['oxford-knee-abridged'].map((q, idx) => (
                                <div key={q.id} className="p-4 bg-white rounded-lg border border-slate-200">
                                    <Label className="font-medium text-slate-800 mb-3 block">
                                        {idx + 1}. {q.question}
                                    </Label>
                                    <RadioGroup
                                        value={(questionnaire_scores.oxford_knee_abridged_answers || {})[q.id]?.toString()}
                                        onValueChange={(value) => {
                                            const currentAnswers = questionnaire_scores.oxford_knee_abridged_answers || {};
                                            const newAnswers = { ...currentAnswers, [q.id]: parseInt(value) };
                                            updateQuestionnaireScores({ oxford_knee_abridged_answers: newAnswers });
                                            
                                            // Calculate score if all questions answered (6 questions)
                                            if (Object.keys(newAnswers).length === questions['oxford-knee-abridged'].length) {
                                                const total = Object.values(newAnswers).reduce((sum, val) => sum + val, 0);
                                                updateQuestionnaireScores({ oxford_knee_abridged_score: total });
                                            }
                                        }}
                                        className="space-y-2"
                                    >
                                        {q.options.map((option, optIdx) => (
                                            <div
                                                key={optIdx}
                                                className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer ${
                                                    (questionnaire_scores.oxford_knee_abridged_answers || {})[q.id] === (4 - optIdx)
                                                        ? 'border-blue-500 bg-blue-50'
                                                        : 'border-slate-200 bg-white hover:border-slate-300'
                                                }`}
                                                onClick={() => {
                                                    const currentAnswers = questionnaire_scores.oxford_knee_abridged_answers || {};
                                                    const newAnswers = { ...currentAnswers, [q.id]: 4 - optIdx };
                                                    updateQuestionnaireScores({ oxford_knee_abridged_answers: newAnswers });
                                                    
                                                    if (Object.keys(newAnswers).length === questions['oxford-knee-abridged'].length) {
                                                        const total = Object.values(newAnswers).reduce((sum, val) => sum + val, 0);
                                                        updateQuestionnaireScores({ oxford_knee_abridged_score: total });
                                                    }
                                                }}
                                            >
                                                <RadioGroupItem value={(4 - optIdx).toString()} id={`oks-abr-${q.id}-${optIdx}`} />
                                                <Label className="flex-1 cursor-pointer text-sm">
                                                    {option}
                                                    <span className="text-xs text-slate-400 ml-2">({4 - optIdx} pts)</span>
                                                </Label>
                                            </div>
                                        ))}
                                    </RadioGroup>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* ASES - American Shoulder and Elbow Surgeons Score - ABRIDGED (for PJP) */}
            {questionnaireTypes.includes('ases-abridged') && questions['ases-abridged'] && (
                <Card className="card-clinical border-blue-200 bg-blue-50/30">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            <ClipboardList className="w-5 h-5 text-blue-600" />
                            Shoulder Score - Abridged (PJP)
                            <span className="text-xs font-normal text-red-500 ml-2">*Required</span>
                        </CardTitle>
                        <CardDescription>
                            6-item questionnaire for prosthetic shoulder assessment. Score 0-24.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Score display */}
                        {questionnaire_scores.ases_abridged_score !== undefined && (
                            <div className={`p-4 rounded-lg border ${
                                questionnaire_scores.ases_abridged_score <= 12 ? 'bg-rose-50 border-rose-200' :
                                questionnaire_scores.ases_abridged_score <= 18 ? 'bg-amber-50 border-amber-200' :
                                'bg-emerald-50 border-emerald-200'
                            }`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className={`font-semibold ${
                                            questionnaire_scores.ases_abridged_score <= 12 ? 'text-rose-600' :
                                            questionnaire_scores.ases_abridged_score <= 18 ? 'text-amber-600' :
                                            'text-emerald-600'
                                        }`}>
                                            {questionnaire_scores.ases_abridged_score <= 12 ? 'Significant prosthetic dysfunction' :
                                             questionnaire_scores.ases_abridged_score <= 18 ? 'Moderate prosthetic dysfunction' :
                                             'Mild/Good prosthetic function'}
                                        </p>
                                        <p className="text-xs text-slate-500">Abridged Shoulder Score: Higher = better function (0-24)</p>
                                    </div>
                                    <div className={`text-3xl font-bold ${
                                        questionnaire_scores.ases_abridged_score <= 12 ? 'text-rose-600' :
                                        questionnaire_scores.ases_abridged_score <= 18 ? 'text-amber-600' :
                                        'text-emerald-600'
                                    }`}>
                                        {questionnaire_scores.ases_abridged_score}/24
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Questions */}
                        <div className="space-y-4">
                            {questions['ases-abridged'].map((q, idx) => (
                                <div key={q.id} className="p-4 bg-white rounded-lg border border-slate-200">
                                    <Label className="font-medium text-slate-800 mb-3 block">
                                        {idx + 1}. {q.question}
                                    </Label>
                                    <RadioGroup
                                        value={(questionnaire_scores.ases_abridged_answers || {})[q.id]?.toString()}
                                        onValueChange={(value) => {
                                            const currentAnswers = questionnaire_scores.ases_abridged_answers || {};
                                            const newAnswers = { ...currentAnswers, [q.id]: parseInt(value) };
                                            updateQuestionnaireScores({ ases_abridged_answers: newAnswers });
                                            
                                            // Calculate score if all questions answered (6 questions)
                                            if (Object.keys(newAnswers).length === questions['ases-abridged'].length) {
                                                const total = Object.values(newAnswers).reduce((sum, val) => sum + val, 0);
                                                updateQuestionnaireScores({ ases_abridged_score: total });
                                            }
                                        }}
                                        className="space-y-2"
                                    >
                                        {q.options.map((option, optIdx) => (
                                            <div
                                                key={optIdx}
                                                className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer ${
                                                    (questionnaire_scores.ases_abridged_answers || {})[q.id] === (4 - optIdx)
                                                        ? 'border-blue-500 bg-blue-50'
                                                        : 'border-slate-200 bg-white hover:border-slate-300'
                                                }`}
                                                onClick={() => {
                                                    const currentAnswers = questionnaire_scores.ases_abridged_answers || {};
                                                    const newAnswers = { ...currentAnswers, [q.id]: 4 - optIdx };
                                                    updateQuestionnaireScores({ ases_abridged_answers: newAnswers });
                                                    
                                                    if (Object.keys(newAnswers).length === questions['ases-abridged'].length) {
                                                        const total = Object.values(newAnswers).reduce((sum, val) => sum + val, 0);
                                                        updateQuestionnaireScores({ ases_abridged_score: total });
                                                    }
                                                }}
                                            >
                                                <RadioGroupItem value={(4 - optIdx).toString()} id={`ases-abr-${q.id}-${optIdx}`} />
                                                <Label className="flex-1 cursor-pointer text-sm">
                                                    {option}
                                                    <span className="text-xs text-slate-400 ml-2">({4 - optIdx} pts)</span>
                                                </Label>
                                            </div>
                                        ))}
                                    </RadioGroup>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* SPADI - Shoulder Pain and Disability Index */}
            {questionnaireTypes.includes('spadi') && (
                <Card className="card-clinical">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            <ClipboardList className="w-5 h-5 text-teal-600" />
                            SPADI - Shoulder Pain and Disability Index
                        </CardTitle>
                        <CardDescription>
                            Rate each item from 0 (no pain/difficulty) to 10 (worst pain/unable to do). 
                            Higher total score = greater disability.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Score display */}
                        {questionnaire_scores.spadi_score !== undefined && (
                            <div className={`p-4 rounded-lg border ${
                                questionnaire_scores.spadi_score >= 50 ? 'bg-rose-50 border-rose-200' :
                                questionnaire_scores.spadi_score >= 30 ? 'bg-amber-50 border-amber-200' :
                                'bg-emerald-50 border-emerald-200'
                            }`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className={`font-semibold ${getSPADIInterpretation(questionnaire_scores.spadi_score)?.color}`}>
                                            {getSPADIInterpretation(questionnaire_scores.spadi_score)?.text}
                                        </p>
                                        <p className="text-xs text-slate-500">SPADI Score: Higher = worse function (0-100)</p>
                                    </div>
                                    <div className={`text-3xl font-bold ${getSPADIInterpretation(questionnaire_scores.spadi_score)?.color}`}>
                                        {questionnaire_scores.spadi_score}%
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Pain subscale */}
                        <div className="space-y-4">
                            <h4 className="font-semibold text-slate-800">Pain Scale</h4>
                            <p className="text-sm text-slate-500">How severe is your pain...</p>
                            {SPADI_QUESTIONS.pain.map((q) => (
                                <div key={q.id} className="space-y-2">
                                    <Label className="text-sm">{q.question}</Label>
                                    <Slider
                                        value={[(questionnaire_scores.spadi_pain || {})[q.id] || 0]}
                                        onValueChange={(value) => handleSPADIChange('pain', q.id, value[0])}
                                        max={10}
                                        step={1}
                                        className="w-full"
                                        data-testid={`spadi-pain-${q.id}`}
                                    />
                                    <div className="flex justify-between text-xs text-slate-400">
                                        <span>0 - No pain</span>
                                        <span>10 - Worst pain</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Disability subscale */}
                        <div className="space-y-4">
                            <h4 className="font-semibold text-slate-800">Disability Scale</h4>
                            <p className="text-sm text-slate-500">How much difficulty do you have...</p>
                            {SPADI_QUESTIONS.disability.map((q) => (
                                <div key={q.id} className="space-y-2">
                                    <Label className="text-sm">{q.question}</Label>
                                    <Slider
                                        value={[(questionnaire_scores.spadi_disability || {})[q.id] || 0]}
                                        onValueChange={(value) => handleSPADIChange('disability', q.id, value[0])}
                                        max={10}
                                        step={1}
                                        className="w-full"
                                        data-testid={`spadi-disability-${q.id}`}
                                    />
                                    <div className="flex justify-between text-xs text-slate-400">
                                        <span>0 - No difficulty</span>
                                        <span>10 - Unable to do</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* WOSI - Western Ontario Shoulder Instability Index */}
            {questionnaireTypes.includes('wosi') && (
                <Card className="card-clinical">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            <ClipboardList className="w-5 h-5 text-teal-600" />
                            WOSI - Western Ontario Shoulder Instability Index
                        </CardTitle>
                        <CardDescription>
                            Rate each item from 0 (no symptoms) to 100 (extreme symptoms). For shoulder instability.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {questionnaire_scores.wosi_score !== undefined && (
                            <div className={`p-4 rounded-lg border ${
                                questionnaire_scores.wosi_score >= 50 ? 'bg-rose-50 border-rose-200' :
                                questionnaire_scores.wosi_score >= 30 ? 'bg-amber-50 border-amber-200' :
                                'bg-emerald-50 border-emerald-200'
                            }`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className={`font-semibold ${getWOSIInterpretation(questionnaire_scores.wosi_score)?.color}`}>
                                            {getWOSIInterpretation(questionnaire_scores.wosi_score)?.text}
                                        </p>
                                    </div>
                                    <div className={`text-3xl font-bold ${getWOSIInterpretation(questionnaire_scores.wosi_score)?.color}`}>
                                        {questionnaire_scores.wosi_score}%
                                    </div>
                                </div>
                            </div>
                        )}

                        {Object.entries(WOSI_QUESTIONS).map(([section, questions]) => (
                            <div key={section} className="space-y-4">
                                <h4 className="font-semibold text-slate-800 capitalize">{section} Symptoms</h4>
                                {questions.map((q) => (
                                    <div key={q.id} className="space-y-2">
                                        <Label className="text-sm">{q.question}</Label>
                                        <Slider
                                            value={[(questionnaire_scores[`wosi_${section}`] || {})[q.id] || 0]}
                                            onValueChange={(value) => handleWOSIChange(section, q.id, value[0])}
                                            max={100}
                                            step={5}
                                            className="w-full"
                                            data-testid={`wosi-${section}-${q.id}`}
                                        />
                                        <div className="flex justify-between text-xs text-slate-400">
                                            <span>0 - No symptoms</span>
                                            <span>100 - Extreme</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}

            {/* MEPS - Mayo Elbow Performance Score */}
            {questionnaireTypes.includes('meps') && (
                <Card className="card-clinical">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            <ClipboardList className="w-5 h-5 text-teal-600" />
                            Mayo Elbow Performance Score (MEPS)
                        </CardTitle>
                        <CardDescription>
                            Score 0-100. Referral criteria: Score ≤65 AND conservative treatment exhausted.
                            <span className="text-rose-600 font-medium ml-2">Threshold for referral: ≤65</span>
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {questionnaire_scores.meps_score !== undefined && (
                            <div className={`p-4 rounded-lg border ${
                                questionnaire_scores.meps_score <= 60 ? 'bg-rose-50 border-rose-200' :
                                questionnaire_scores.meps_score <= 75 ? 'bg-amber-50 border-amber-200' :
                                'bg-emerald-50 border-emerald-200'
                            }`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className={`font-semibold ${getMEPSInterpretation(questionnaire_scores.meps_score)?.color}`}>
                                            {getMEPSInterpretation(questionnaire_scores.meps_score)?.text}
                                        </p>
                                        {questionnaire_scores.meps_score <= 65 && (
                                            <p className="text-xs text-rose-600 flex items-center gap-1 mt-1">
                                                <CheckCircle className="w-3 h-3" /> Meets MEPS threshold for referral (≤65)
                                            </p>
                                        )}
                                        {questionnaire_scores.meps_score > 65 && (
                                            <p className="text-xs text-amber-600 flex items-center gap-1 mt-1">
                                                <XCircle className="w-3 h-3" /> Does not meet MEPS threshold (needs ≤65)
                                            </p>
                                        )}
                                    </div>
                                    <div className={`text-3xl font-bold ${getMEPSInterpretation(questionnaire_scores.meps_score)?.color}`}>
                                        {questionnaire_scores.meps_score}/100
                                    </div>
                                </div>
                            </div>
                        )}

                        {MEPS_QUESTIONS.map((q) => (
                            <div key={q.id} className="p-4 bg-slate-50 rounded-lg">
                                <Label className="font-semibold text-slate-800 mb-3 block">{q.question}</Label>
                                <RadioGroup
                                    value={(questionnaire_scores.meps_answers || {})[q.id]?.toString()}
                                    onValueChange={(value) => handleMEPSChange(q.id, parseInt(value))}
                                    className="space-y-2"
                                >
                                    {q.options.map((option, idx) => (
                                        <div
                                            key={idx}
                                            className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer ${
                                                (questionnaire_scores.meps_answers || {})[q.id] === option.score
                                                    ? 'border-teal-500 bg-teal-50'
                                                    : 'border-slate-200 bg-white hover:border-slate-300'
                                            }`}
                                            onClick={() => handleMEPSChange(q.id, option.score)}
                                        >
                                            <RadioGroupItem value={option.score.toString()} id={`meps-${q.id}-${idx}`} />
                                            <Label className="flex-1 cursor-pointer text-sm">
                                                {option.label}
                                                <span className="text-xs text-slate-400 ml-2">({option.score} pts)</span>
                                            </Label>
                                        </div>
                                    ))}
                                </RadioGroup>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}

            {/* CTS-6 - Carpal Tunnel Syndrome */}
            {questionnaireTypes.includes('cts6') && (
                <Card className="card-clinical">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            <ClipboardList className="w-5 h-5 text-teal-600" />
                            CTS-6 - Carpal Tunnel Syndrome Assessment
                        </CardTitle>
                        <CardDescription>
                            6-item clinical assessment for carpal tunnel syndrome probability.
                            Score ≥12 indicates high probability.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {questionnaire_scores.cts6_score !== undefined && (
                            <div className={`p-4 rounded-lg border ${
                                questionnaire_scores.cts6_score >= 12 ? 'bg-rose-50 border-rose-200' :
                                questionnaire_scores.cts6_score >= 8 ? 'bg-amber-50 border-amber-200' :
                                'bg-emerald-50 border-emerald-200'
                            }`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className={`font-semibold ${getCTS6Interpretation(questionnaire_scores.cts6_score)?.color}`}>
                                            {getCTS6Interpretation(questionnaire_scores.cts6_score)?.text}
                                        </p>
                                        <p className="text-xs text-slate-500">CTS-6 Score: 0-18 (higher = more likely CTS)</p>
                                    </div>
                                    <div className={`text-3xl font-bold ${getCTS6Interpretation(questionnaire_scores.cts6_score)?.color}`}>
                                        {questionnaire_scores.cts6_score}/18
                                    </div>
                                </div>
                            </div>
                        )}

                        {CTS6_QUESTIONS.map((q) => (
                            <div key={q.id} className="p-4 bg-slate-50 rounded-lg">
                                <Label className="font-semibold text-slate-800 mb-3 block">{q.question}</Label>
                                <RadioGroup
                                    value={(questionnaire_scores.cts6_answers || {})[q.id]?.toString()}
                                    onValueChange={(value) => handleCTS6Change(q.id, parseInt(value))}
                                    className="space-y-2"
                                >
                                    {q.options.map((option, idx) => (
                                        <div
                                            key={idx}
                                            className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer ${
                                                (questionnaire_scores.cts6_answers || {})[q.id] === option.score
                                                    ? 'border-teal-500 bg-teal-50'
                                                    : 'border-slate-200 bg-white hover:border-slate-300'
                                            }`}
                                            onClick={() => handleCTS6Change(q.id, option.score)}
                                        >
                                            <RadioGroupItem value={option.score.toString()} id={`cts6-${q.id}-${idx}`} />
                                            <Label className="flex-1 cursor-pointer text-sm">
                                                {option.label}
                                            </Label>
                                        </div>
                                    ))}
                                </RadioGroup>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}

            {/* QuickDASH - Disabilities of the Arm, Shoulder and Hand */}
            {questionnaireTypes.includes('quickdash') && (
                <Card className="card-clinical">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            <ClipboardList className="w-5 h-5 text-teal-600" />
                            QuickDASH - Hand & Upper Extremity Function
                            <span className="text-xs font-normal text-red-500 ml-2">*Required</span>
                        </CardTitle>
                        <CardDescription>
                            11-item questionnaire measuring arm, shoulder and hand disability.
                            Score 0-100 (higher = greater disability). Referral threshold: ≥40
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {questionnaire_scores.quickdash_score !== undefined && (
                            <div className={`p-4 rounded-lg border ${
                                questionnaire_scores.quickdash_score >= 60 ? 'bg-rose-50 border-rose-200' :
                                questionnaire_scores.quickdash_score >= 40 ? 'bg-amber-50 border-amber-200' :
                                questionnaire_scores.quickdash_score >= 20 ? 'bg-amber-50 border-amber-200' :
                                'bg-emerald-50 border-emerald-200'
                            }`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className={`font-semibold ${getQuickDASHInterpretation(questionnaire_scores.quickdash_score)?.color}`}>
                                            {getQuickDASHInterpretation(questionnaire_scores.quickdash_score)?.text}
                                        </p>
                                        <p className="text-xs text-slate-500">QuickDASH Score: 0-100 (higher = greater disability)</p>
                                    </div>
                                    <div className={`text-3xl font-bold ${getQuickDASHInterpretation(questionnaire_scores.quickdash_score)?.color}`}>
                                        {questionnaire_scores.quickdash_score}/100
                                    </div>
                                </div>
                            </div>
                        )}

                        <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                            Please rate the patient's ability to perform the following activities in the last week:
                        </p>

                        {QUICKDASH_QUESTIONS.map((q, idx) => (
                            <div key={q.id} className="p-4 bg-slate-50 rounded-lg">
                                <Label className="font-semibold text-slate-800 mb-3 block">
                                    {idx + 1}. {q.question}
                                </Label>
                                <RadioGroup
                                    value={(questionnaire_scores.quickdash_answers || {})[q.id]?.toString()}
                                    onValueChange={(value) => handleQuickDASHChange(q.id, parseInt(value))}
                                    className="space-y-2"
                                >
                                    {q.options.map((option, optIdx) => (
                                        <div
                                            key={optIdx}
                                            className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer ${
                                                (questionnaire_scores.quickdash_answers || {})[q.id] === option.score
                                                    ? 'border-teal-500 bg-teal-50'
                                                    : 'border-slate-200 bg-white hover:border-slate-300'
                                            }`}
                                            onClick={() => handleQuickDASHChange(q.id, option.score)}
                                        >
                                            <RadioGroupItem value={option.score.toString()} id={`quickdash-${q.id}-${optIdx}`} />
                                            <Label className="flex-1 cursor-pointer text-sm">
                                                {option.label}
                                            </Label>
                                        </div>
                                    ))}
                                </RadioGroup>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}

            {/* No questionnaire message */}
            {questionnaireTypes.length === 0 && !loading && (
                <Card className="card-clinical bg-slate-50">
                    <CardContent className="pt-6">
                        <div className="text-center py-8">
                            <Info className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                            <h4 className="font-semibold text-slate-700 mb-2">
                                No Standardised Questionnaire Required
                            </h4>
                            <p className="text-sm text-slate-500">
                                The selected condition ({condition}) does not require a formal scoring questionnaire.
                                Please proceed to the next step for clinical findings.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
